import {
  createTransportClient,
  getTransportTokenForOrganization,
} from "@/transport"

export interface PaidOverageIdentity {
  clerkOrganizationId: string
  organizationSlug: string
  actorId: string
  expectedOrganizationId?: string
  getToken: (options?: {
    organizationId?: string
    skipCache?: boolean
  }) => Promise<string | null>
}

function tokenSubject(token: string | null): unknown {
  try {
    const payload = token?.split(".")[1]
    if (!payload) {
      return null
    }
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
    const claims: unknown = JSON.parse(globalThis.atob(padded))
    return claims !== null && typeof claims === "object" && "sub" in claims
      ? claims.sub
      : null
  } catch {
    return null
  }
}

/** Checks UI/session consistency; the transport server verifies the bearer signature. */
export async function connectPaidOverageTransport(
  identity: PaidOverageIdentity,
  signal: AbortSignal,
) {
  if (
    !identity.clerkOrganizationId ||
    !identity.organizationSlug ||
    !identity.actorId ||
    identity.actorId.length > 512
  ) {
    throw new Error("An explicit signed organization and member are required.")
  }
  signal.throwIfAborted()
  const client = createTransportClient({
    getToken: async () => {
      signal.throwIfAborted()
      const token = await getTransportTokenForOrganization({
        getToken: identity.getToken,
        organizationId: identity.clerkOrganizationId,
        organizationSlug: identity.organizationSlug,
        requireOrganizationClaims: true,
      })
      signal.throwIfAborted()
      if (tokenSubject(token) !== identity.actorId) {
        throw new Error("The signed member changed; reload this review.")
      }
      return token
    },
    getOrganizationContext: () => ({ slug: identity.organizationSlug }),
  })
  const { organization } = await client.operations.getOrganizationRecord(
    { skip_cache: true },
    { signal },
  )
  signal.throwIfAborted()
  if (
    organization?.clerk_id !== identity.clerkOrganizationId ||
    typeof organization.id !== "string" ||
    organization.id.length < 1 ||
    organization.id.length > 512 ||
    (identity.expectedOrganizationId !== undefined &&
      organization.id !== identity.expectedOrganizationId)
  ) {
    throw new Error("The organization identity could not be verified.")
  }
  return {
    client,
    organizationId: organization.id,
    options: {
      headers: { "X-Coval-Organization-Id": organization.id },
      signal,
    },
  }
}
